import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { buildSavedSegmentationPreview } from "@/predictions/savedSegmentationPreview";
import {
  resolveSavedSegmentationPreview,
  type LoadPreviewGeometry,
} from "@/predictions/resolveSavedSegmentationPreview";
import { PREDICTION_MASK_SHORTCUT } from "@/constants/viewerActions";
import type { AnnotationRecordResponse } from "@/hooks/useLabelingQueries";
import {
  AI_ACCEPTED_BADGE,
  DISABLED_ACTION_CLASS,
  SavedSegmentationCard,
} from "@/components/predictions/SavedSegmentationCard";
import { SavedSegmentationMeasurements } from "@/components/predictions/SavedSegmentationMeasurements";
import { SegmentationVolume } from "./SegmentationVolume";
import type { StudyMetadataResponse } from "@/utils/dicomMetadataLoader";
import type { CaseLockReason } from "@/utils/caseStatus";

import { renderSegmentationPreviewThumbnail } from "../../predictions/segmentationPreviewThumbnail";
import {
  persistedSegmentationCategory,
  formatSegmentationCondition,
} from "../../predictions/segmentationCondition";
import type { SegmentationPreview } from "../../predictions/segmentationPreviews";
import { emit } from "../../lib/viewerBus";
import { describeVerdictFailure, verdictFailureToast } from "../../predictions/verdictError";
import { ReaderConfidenceInput } from "../labeling/ReaderConfidenceInput";
import { CONFIDENCE_SEPARATOR_CLASS, CardConfidenceRow } from "./CardConfidenceRow";
import {
  READER_CONFIDENCE_HINT,
  READER_CONFIDENCE_KEY,
  parseReaderConfidence,
} from "../../utils/readerConfidence";

export type SegmentationReviewDecision = "approved" | "denied";
export type SegmentationVerdict = SegmentationReviewDecision | "pending";
/** Why an AI card's contour is hidden, so its editor could never open (ENG-186). */
export type EditHiddenReason = "declined" | "masks-hidden";

export interface SegmentationPreviewRailProps {
  previews: SegmentationPreview[];
  studyMetadata?: StudyMetadataResponse | null;
  loadFrameGeometry?: LoadPreviewGeometry;
  /** Enable native-series sampling only for the segmentation study workflow. */
  resampleSavedVolume?: boolean;
  conditionLabels?: Readonly<Record<string, string>>;
  /** Current-case saved annotations without an existing prediction card. */
  savedAnnotations?: readonly AnnotationRecordResponse[];
  predictionAnnotations?: Readonly<Partial<Record<string, AnnotationRecordResponse>>>;
  onPreviewSelect: (preview: SegmentationPreview) => void;
  onSavedPreviewSelect?: (preview: SegmentationPreview) => void;
  onEditSavedAnnotation?: (
    annotation: AnnotationRecordResponse,
    preview: SegmentationPreview | null,
  ) => void;
  onReviewSavedAnnotation?: (
    annotation: AnnotationRecordResponse,
    review: "accepted" | "declined" | "pending",
  ) => Promise<void>;
  onShowReportTab: () => void;
  showReportTab?: boolean;
  onAddAnnotation?: () => void;
  onEditPreview?: (preview: SegmentationPreview) => void;
  /** readerConfidence is the card's rating when requireReaderConfidence is set
   *  and the card is not being edited (the editor supplies its own then). */
  onAcceptPreview?: (
    preview: SegmentationPreview,
    readerConfidence?: number,
  ) => Promise<void> | void;
  /** Segmentation study: Accept needs a 1–100 confidence rating on the card (ENG-262). */
  requireReaderConfidence?: boolean;
  /** Segmentation study: re-rate a saved card without the editor (ENG-277). Rejects on failure. */
  onSaveConfidence?: (
    annotation: AnnotationRecordResponse,
    readerConfidence: number,
  ) => Promise<void>;
  onDeclinePreview?: (preview: SegmentationPreview) => Promise<void> | void;
  /** Global mask-overlay visibility; renders a Hide/Show masks control when
   * onToggleMasks is provided so readers can inspect the raw pixels without
   * rejecting a finding. */
  masksVisible?: boolean;
  onToggleMasks?: () => void;
  onUndoPreview?: (preview: SegmentationPreview) => Promise<void> | void;
  verdictsDisabled?: boolean;
  /** Cards whose confirmed save is still reloading; only these stay locked (ENG-229). */
  lockedPreviewIds?: ReadonlySet<string>;
  /** AI cards whose outline isn't drawn yet; locked so a click can't land before it (ENG-269). */
  maskLoadingPreviewIds?: ReadonlySet<string>;
  /** Epoch ms at which each loading card stops waiting; drives its countdown. */
  maskLoadingDeadlines?: Readonly<Record<string, number>>;
  /** AI cards whose outline didn't load in time; a reload may fix it. Verdicts stay
   *  open; Edit and the key-slice jump need the outline (ENG-269). */
  maskFailedPreviewIds?: ReadonlySet<string>;
  /** AI cards whose outline can't be drawn on any reload (files don't match the
   *  mask, or no segmentation volume). Same gates as failed (ENG-269). */
  maskUnavailablePreviewIds?: ReadonlySet<string>;
  /** Edit-only gate (saved annotations not loaded or failed); the parent's
   *  onEditPreview refuses on the same condition (ENG-187). */
  editDisabled?: boolean;
  /** Source images still streaming. The parent also folds this into
   *  verdictsDisabled, so here it only picks the card hint (ENG-186). */
  imagesLoading?: boolean;
  /** Per-card Edit gate with its hint; the parent's onEditPreview refuses on
   *  the same map (ENG-186 step 3). */
  editHiddenReasons?: Readonly<Partial<Record<string, EditHiddenReason>>>;
  verdicts?: Readonly<Record<string, SegmentationVerdict>>;
  onVerdictChange?: (previewId: string, verdict: SegmentationVerdict) => void;
  annotationContent?: ReactNode;
  statusContent?: ReactNode;
  annotationActive?: boolean;
  /** Prediction card whose candidate the open editor is changing, if any. */
  editingPreviewId?: string | null;
  /** AI card whose decision is unconfirmed; every other card dims until Retry decision. */
  unconfirmedPreviewId?: string | null;
  /** False when the parent study shell supplies the reporter-style heading. */
  showHeading?: boolean;
  annotationPlaceholder?: {
    active: boolean;
  };
  /** Segmentation study: dim the card list and block it with an overlay while set. */
  lockReason?: CaseLockReason | null;
  loadFrame: (
    seriesUid: string,
    sliceIndex: number,
    window: { windowWidth: number; windowCenter: number },
  ) => Promise<HTMLCanvasElement>;
}

export const EDITING_UNDO_ACCEPT_MESSAGE =
  "To keep your changes, click Save in the editor. To discard them, click Decline or Cancel.";
export const CASE_LOCK_TEXT: Record<CaseLockReason, { title: string; undoHint: string }> = {
  complete: {
    title: "Case marked complete — no edits can be made.",
    undoHint: 'Please press "Completed — Unmark" to undo completion, if you need to re-edit.',
  },
  unassessable: {
    title: "Case marked unassessable — no edits can be made.",
    undoHint: 'Please press "Clear my flag" to undo unassessable, if you need to re-edit.',
  },
};
const TAB_LABEL_CLASS = "font-mono text-[10px] uppercase tracking-[0.14em] px-3 py-2";
const EMPTY_ANNOTATIONS: readonly AnnotationRecordResponse[] = [];
const EMPTY_LINKS: Readonly<Partial<Record<string, AnnotationRecordResponse>>> = {};
const EMPTY_LABELS: Readonly<Record<string, string>> = {};
const NO_LOCKED_PREVIEWS: ReadonlySet<string> = new Set();
const NO_DEADLINES: Readonly<Record<string, number>> = {};

function secondsLeft(deadline: number): number {
  return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
}

/** "Loading mask" hint with the seconds left before the card stops waiting. */
function MaskLoadingHint({ deadline }: { deadline?: number }) {
  const [seconds, setSeconds] = useState(() => (deadline == null ? null : secondsLeft(deadline)));
  useEffect(() => {
    if (deadline == null) return;
    setSeconds(secondsLeft(deadline));
    const tick = setInterval(() => setSeconds(secondsLeft(deadline)), 1000);
    return () => clearInterval(tick);
  }, [deadline]);
  return <>Loading mask, please wait…{seconds == null ? "" : ` (${seconds} sec)`}</>;
}

function formatMm(value: number | null): string {
  return value == null ? "—" : value.toFixed(1);
}

export function SegmentationPreviewRail({
  previews,
  savedAnnotations = EMPTY_ANNOTATIONS,
  studyMetadata,
  conditionLabels = EMPTY_LABELS,
  predictionAnnotations = EMPTY_LINKS,
  onPreviewSelect,
  onSavedPreviewSelect,
  onEditSavedAnnotation,
  onReviewSavedAnnotation,
  onShowReportTab,
  showReportTab = false,
  onAddAnnotation,
  onEditPreview,
  onAcceptPreview,
  requireReaderConfidence = false,
  onSaveConfidence,
  onDeclinePreview,
  masksVisible = true,
  onToggleMasks,
  onUndoPreview,
  verdictsDisabled = false,
  lockedPreviewIds = NO_LOCKED_PREVIEWS,
  maskLoadingPreviewIds = NO_LOCKED_PREVIEWS,
  maskLoadingDeadlines = NO_DEADLINES,
  maskFailedPreviewIds = NO_LOCKED_PREVIEWS,
  maskUnavailablePreviewIds = NO_LOCKED_PREVIEWS,
  editDisabled = false,
  imagesLoading = false,
  editHiddenReasons = {},
  verdicts = {},
  onVerdictChange,
  annotationContent,
  statusContent,
  annotationActive = false,
  editingPreviewId = null,
  unconfirmedPreviewId = null,
  showHeading = true,
  annotationPlaceholder,
  lockReason = null,
  loadFrame,
  loadFrameGeometry,
  resampleSavedVolume = false,
}: SegmentationPreviewRailProps) {
  const lockText = lockReason ? CASE_LOCK_TEXT[lockReason] : null;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Saved card whose Edit opened the editor; only meaningful while annotationActive.
  const [editingSavedId, setEditingSavedId] = useState<string | null>(null);
  const [thumbnailResults, setThumbnailResults] = useState<
    Record<string, { key: string; value: string | null; loader: typeof loadFrame }>
  >({});
  const thumbnailCache = useMemo(
    () => new Map<string, { key: string; promise: Promise<string> }>(),
    [loadFrame],
  );
  const geometryCache = useMemo(
    () => new Map<string, { key: string; promise: Promise<SegmentationPreview | null> }>(),
    [loadFrameGeometry, studyMetadata, resampleSavedVolume],
  );
  const [pendingVerdictIds, setPendingVerdictIds] = useState<Set<string>>(new Set());
  const [confidenceDrafts, setConfidenceDrafts] = useState<Record<string, string>>({});
  const draftRef = useRef<HTMLDivElement>(null);
  const newAnnotationActive = !!annotationPlaceholder?.active;
  const cardCount = previews.length + savedAnnotations.length;
  const orderedSaved = useMemo(
    () =>
      [...savedAnnotations].sort(
        (a, b) => a.segment_index - b.segment_index || a.annotated_at.localeCompare(b.annotated_at),
      ),
    [savedAnnotations],
  );
  const cardRequests = useMemo(() => {
    const result: Record<string, SegmentationPreview | null> = {};
    const savedRequests: {
      id: string;
      annotation: AnnotationRecordResponse;
      condition: string;
      sequence: number;
      key: string;
    }[] = [];
    const addSaved = (
      id: string,
      annotation: AnnotationRecordResponse,
      condition: string,
      sequence: number,
    ) => {
      const key = JSON.stringify([
        annotation.project_id,
        annotation.study_instance_uid,
        annotation.annotation_id,
        annotation.series_instance_uid,
        annotation.segment_index,
        annotation.card_geometry,
        annotation.segmentation_stats?.volume_mm3,
        condition,
        sequence,
      ]);
      savedRequests.push({ id, annotation, condition, sequence, key });
      result[id] = loadFrameGeometry
        ? null
        : buildSavedSegmentationPreview(annotation, studyMetadata, condition, sequence);
    };
    for (const preview of previews) {
      const saved = predictionAnnotations[preview.id];
      if (saved) addSaved(preview.id, saved, preview.condition, preview.segmentIndex);
      else result[preview.id] = preview;
    }
    orderedSaved.forEach((annotation, index) => {
      const condition =
        conditionLabels[annotation.category] ??
        annotation.attributes.ai_prediction_condition ??
        annotation.category;
      addSaved(
        `saved-${annotation.annotation_id}`,
        annotation,
        condition,
        previews.length + index + 1,
      );
    });
    return { result, savedRequests };
  }, [
    previews,
    predictionAnnotations,
    orderedSaved,
    studyMetadata,
    conditionLabels,
    loadFrameGeometry,
  ]);
  const [resolvedCards, setResolvedCards] = useState<
    Record<
      string,
      {
        key: string;
        preview: SegmentationPreview | null;
        cache: typeof geometryCache;
      }
    >
  >({});
  const cardPreviews = { ...cardRequests.result };
  for (const request of cardRequests.savedRequests) {
    const resolved = resolvedCards[request.id];
    if (resolved?.key === request.key && resolved.cache === geometryCache) {
      cardPreviews[request.id] = resolved.preview;
    }
  }
  useEffect(() => {
    if (!loadFrameGeometry) return;
    let cancelled = false;
    const ids = new Set(cardRequests.savedRequests.map((request) => request.id));
    for (const id of geometryCache.keys()) if (!ids.has(id)) geometryCache.delete(id);
    for (const request of cardRequests.savedRequests) {
      let cached = geometryCache.get(request.id);
      if (!cached || cached.key !== request.key) {
        cached = {
          key: request.key,
          promise: resolveSavedSegmentationPreview(
            request.annotation,
            studyMetadata,
            request.condition,
            request.sequence,
            loadFrameGeometry,
            resampleSavedVolume,
          ),
        };
        geometryCache.set(request.id, cached);
      }
      cached.promise
        .then((preview) => {
          if (cancelled) return;
          setResolvedCards((current) => ({
            ...Object.fromEntries(Object.entries(current).filter(([id]) => ids.has(id))),
            [request.id]: { key: request.key, preview, cache: geometryCache },
          }));
        })
        .catch((error) => {
          if (geometryCache.get(request.id) === cached) geometryCache.delete(request.id);
          console.error("[SegmentationPreviewRail] frame geometry load failed", error);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [cardRequests, studyMetadata, loadFrameGeometry, geometryCache, resampleSavedVolume]);

  // Selection and a new-annotation placeholder are mutually exclusive. When
  // Add Annotation starts, clear the previously selected AI card so the rail
  // never presents two simultaneous blue highlights.
  useEffect(() => {
    if (newAnnotationActive) {
      setSelectedId(null);
      draftRef.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [newAnnotationActive]);

  const thumbnailSnapshot = JSON.stringify(cardPreviews);
  const thumbnails: Record<string, string | null | undefined> = {};
  for (const [id, preview] of Object.entries(cardPreviews)) {
    const result = thumbnailResults[id];
    if (preview && result?.key === JSON.stringify(preview) && result.loader === loadFrame)
      thumbnails[id] = result.value;
  }
  useEffect(() => {
    let cancelled = false;
    const previews: Record<string, SegmentationPreview | null> = JSON.parse(thumbnailSnapshot);
    const ids = new Set(Object.keys(previews));
    for (const id of thumbnailCache.keys()) if (!ids.has(id)) thumbnailCache.delete(id);
    setSelectedId((current) => (current && ids.has(current) ? current : null));
    for (const [id, preview] of Object.entries(previews)) {
      if (!preview) continue;
      const key = JSON.stringify(preview);
      let cached = thumbnailCache.get(id);
      if (!cached || cached.key !== key) {
        cached = {
          key,
          promise: renderSegmentationPreviewThumbnail(preview, loadFrame).then((canvas) =>
            canvas.toDataURL(),
          ),
        };
        thumbnailCache.set(id, cached);
      }
      cached.promise
        .then((value) => {
          if (!cancelled) {
            setThumbnailResults((current) => ({
              ...Object.fromEntries(Object.entries(current).filter(([id]) => ids.has(id))),
              [id]: { key, value, loader: loadFrame },
            }));
          }
        })
        .catch((error) => {
          console.error("[segmentation-preview-thumbnail] render failed", error);
          if (!cancelled) {
            thumbnailCache.delete(id);
            setThumbnailResults((current) => ({
              ...current,
              [id]: { key, value: null, loader: loadFrame },
            }));
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [thumbnailSnapshot, loadFrame, thumbnailCache]);

  const editingPreview = editingPreviewId
    ? previews.find((preview) => preview.id === editingPreviewId)
    : undefined;
  const editingBannerText = editingPreview
    ? `Editing · ${formatSegmentationCondition(editingPreview.condition)} · ${editingPreview.segmentIndex}`
    : newAnnotationActive
      ? "Editing · New annotation"
      : "Editing · Saved annotation";

  return (
    <div className={`flex flex-col bg-bg-2 text-fg ${lockText ? "min-h-full" : ""}`}>
      <div className="sticky top-0 z-30 shrink-0 bg-bg-2" data-testid="segmentation-controls">
        {statusContent}
        {(showHeading || onToggleMasks) && (
          <div className="flex shrink-0 border-b border-border">
            {showHeading && (
              <div className={`relative ${TAB_LABEL_CLASS} text-fg`}>
                Segmentations · {cardCount}
                <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-signal" />
              </div>
            )}
            {showHeading && showReportTab && (
              <button
                type="button"
                onClick={onShowReportTab}
                className={`${TAB_LABEL_CLASS} text-fg-muted hover:text-fg transition-colors`}
              >
                Report
              </button>
            )}
            {onToggleMasks && (
              <button
                type="button"
                aria-pressed={!masksVisible}
                title={`Toggle AI mask overlay (${PREDICTION_MASK_SHORTCUT})`}
                onClick={onToggleMasks}
                className={`ml-auto my-1.5 mr-2 h-6 px-2 rounded-[2px] border font-mono text-[10px] uppercase tracking-wide transition-colors ${
                  masksVisible
                    ? "border-border-strong text-fg-muted hover:text-fg hover:bg-surface-2"
                    : "border-dashed border-border-strong text-fg-muted"
                }`}
              >
                {masksVisible ? "Hide masks" : "Masks hidden"}
              </button>
            )}
          </div>
        )}
        {onAddAnnotation && !annotationActive && (
          <button
            type="button"
            onClick={onAddAnnotation}
            className="mx-3 mt-3 sidebar-btn-primary h-8 text-[11px]"
          >
            Add Annotation
          </button>
        )}
        {annotationContent && (
          <div
            className={`sticky top-0 z-20 bg-bg-2 pt-2 ${
              annotationActive
                ? "border-y-2 border-signal shadow-[0_4px_12px_rgba(0,0,0,0.35)]"
                : "border-b border-border/50"
            }`}
          >
            {annotationActive && (
              <div
                role="status"
                data-testid="annotation-editor-banner"
                className="mx-2 mb-2 flex items-center gap-2 border-l-4 border-signal bg-signal-tint px-2 py-1.5 font-mono text-[10px] uppercase tracking-wide text-signal-text-tint"
              >
                <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-signal" />
                <span className="truncate">{editingBannerText}</span>
              </div>
            )}
            {annotationContent}
          </div>
        )}
      </div>
      {/* Locked: fill the space under the sticky controls so the message is
          centered in the visible card area, even with a single card. */}
      <div className={`relative ${lockText ? "flex-1 min-h-28" : ""}`}>
        {/* Locked: inert drops the cards from pointer, Tab and Enter; the overlay
          covers them so even key-slice navigation is blocked. */}
        <div
          data-testid="segmentation-card-list"
          className={`p-2 flex flex-col gap-2 ${lockText ? "opacity-25 pointer-events-none select-none" : ""}`}
          aria-disabled={lockText ? true : undefined}
          inert={!!lockText}
        >
          {[...previews]
            .sort((a, b) => a.segmentIndex - b.segmentIndex)
            .map((preview) => {
              const selected = selectedId === preview.id;
              const saved = predictionAnnotations[preview.id];
              const cardPreview = cardPreviews[preview.id];
              const maskUnavailable = maskUnavailablePreviewIds.has(preview.id);
              const maskFailed = maskUnavailable || maskFailedPreviewIds.has(preview.id);
              const maskLoading = maskLoadingPreviewIds.has(preview.id);
              // Loading locks the whole card. Without an outline (failed or
              // unavailable) verdicts stay open; Edit and the key-slice jump don't.
              const maskWaiting = maskFailed || maskLoading;
              const navigate = () => {
                if (annotationActive || !cardPreview || maskWaiting) return;
                setSelectedId(preview.id);
                if (saved) onSavedPreviewSelect?.(cardPreview);
                else onPreviewSelect(cardPreview);
              };
              const { longAxisMm, shortAxisMm } = preview.measurements;
              // The Accept gate uses the prediction's own size; saved display fields must not change it.
              const reportable = longAxisMm != null && shortAxisMm != null;
              // While an editor is open, only the edited card's verdicts stay live: Accept
              // saves the edit first and Decline discards it (ENG-185). Other cards wait.
              const editingThis = annotationActive && editingPreviewId === preview.id;
              const editingOther = annotationActive && !editingThis;
              // Cards not involved in the edit or the unconfirmed decision step back (ENG-186).
              const dimOther =
                editingOther ||
                (unconfirmedPreviewId != null && unconfirmedPreviewId !== preview.id);
              const approved = verdicts[preview.id] === "approved";
              const denied = verdicts[preview.id] === "denied";
              const editHidden = editHiddenReasons[preview.id];
              // Stays focusable so the click can explain how to leave the edit.
              const undoAcceptBlocked = editingThis && approved;
              const label = formatSegmentationCondition(preview.condition);
              // Accept persists an annotation and Edit opens the schema-backed
              // editor; both require the condition's category to exist in the
              // persisted vocabulary. Without it the mask stays view-only —
              // offering the buttons anyway would 422 on save or render a form
              // that can never save.
              const persistable = persistedSegmentationCategory(preview.condition) != null;
              const modified = saved?.attributes.ai_prediction_review === "modified";
              const aiAccepted = saved?.attributes.ai_prediction_review === "accepted";
              const verdictPending = pendingVerdictIds.has(preview.id);
              const cardLocked =
                verdictsDisabled || lockedPreviewIds.has(preview.id) || maskLoading;
              // An unavailable outline means the files don't match the mask, so an
              // Accept could save it on the wrong slices. Undo of an earlier Accept stays.
              const acceptBlocked = maskUnavailable && !approved;
              // The open editor carries the rating while this card is edited (ENG-262).
              const askConfidence = requireReaderConfidence && persistable && !approved;
              const cardConfidence = parseReaderConfidence(confidenceDrafts[preview.id] ?? "");
              const confidenceMissing = askConfidence && !editingThis && cardConfidence == null;
              const acceptDisabled =
                cardLocked ||
                acceptBlocked ||
                !reportable ||
                verdictPending ||
                editingOther ||
                confidenceMissing;
              const editButtonDisabled =
                cardLocked || maskFailed || editDisabled || annotationActive || !!editHidden;
              // A saved card re-rates only when both Accept and Edit are available;
              // while this card is edited, the rating lives in the editor (ENG-277).
              const showSavedConfidence =
                requireReaderConfidence &&
                persistable &&
                approved &&
                !!saved &&
                !editingThis &&
                !!onSaveConfidence;
              const applyVerdict = async (verdict: SegmentationReviewDecision) => {
                if (cardLocked || verdictPending) return;
                if (verdict === "approved" && acceptBlocked) return;
                const nextVerdict: SegmentationVerdict =
                  verdicts[preview.id] === verdict ? "pending" : verdict;
                setPendingVerdictIds((current) => new Set(current).add(preview.id));
                try {
                  if (nextVerdict === "pending") await onUndoPreview?.(preview);
                  else if (nextVerdict === "approved")
                    await (requireReaderConfidence && !editingThis
                      ? onAcceptPreview?.(preview, cardConfidence ?? undefined)
                      : onAcceptPreview?.(preview));
                  else await onDeclinePreview?.(preview);
                  onVerdictChange?.(preview.id, nextVerdict);
                } catch (error) {
                  const action =
                    nextVerdict === "pending"
                      ? "undo"
                      : nextVerdict === "approved"
                        ? "accept"
                        : "decline";
                  const failure = describeVerdictFailure(error);
                  console.error(
                    `[segmentation-preview] ${action} failed`,
                    {
                      case: failure.kind,
                      code: failure.code,
                      status: failure.status,
                      detail: failure.detail,
                      previewId: preview.id,
                      condition: preview.condition,
                      sourceSegmentIndex: preview.sourceSegmentIndex,
                    },
                    error,
                  );
                  emit("toast", { message: verdictFailureToast(action, failure) });
                } finally {
                  setPendingVerdictIds((current) => {
                    const next = new Set(current);
                    next.delete(preview.id);
                    return next;
                  });
                }
              };
              return (
                <div
                  key={preview.id}
                  role="button"
                  tabIndex={cardPreview && !annotationActive && !maskWaiting ? 0 : -1}
                  // Only loading marks the card disabled: aria-disabled carries to the
                  // Accept/Decline inside it, which a failed card keeps open.
                  aria-disabled={!cardPreview || annotationActive || maskLoading}
                  data-testid="segmentation-preview-card"
                  onClick={navigate}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      navigate();
                    }
                  }}
                  data-editing={editingThis || undefined}
                  // Edited card: signal border + left rule (active); others fade (de-emphasized).
                  className={`flex gap-3 p-2.5 text-left bg-surface border rounded-[2px] transition-[border-color,opacity] ${
                    editingThis
                      ? "border-signal border-l-4"
                      : selected
                        ? "border-signal"
                        : "border-border hover:border-border-strong"
                  } ${dimOther ? "opacity-40" : denied ? "opacity-50" : ""}`}
                >
                  <div className="shrink-0 w-[132px] h-[132px] rounded-[2px] bg-bg-2 overflow-hidden flex items-center justify-center">
                    {thumbnails[preview.id] ? (
                      <img
                        src={thumbnails[preview.id] ?? undefined}
                        alt={`${label} segmentation preview on slice ${cardPreview?.sliceNumber}`}
                        className="w-full h-full"
                      />
                    ) : (
                      <span className="font-mono text-[10px] text-fg-muted">
                        {!cardPreview || thumbnails[preview.id] === null
                          ? "Preview unavailable"
                          : "Loading preview…"}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col gap-1.5 pt-0.5">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-mono text-xs text-fg">
                        {label} · {preview.segmentIndex}
                      </span>
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${
                          modified
                            ? "bg-amber-500/15 text-amber-300"
                            : aiAccepted
                              ? AI_ACCEPTED_BADGE.className
                              : "bg-signal-tint text-signal-text-tint"
                        }`}
                      >
                        {modified
                          ? "Modified"
                          : aiAccepted
                            ? AI_ACCEPTED_BADGE.label
                            : saved
                              ? "Saved annotation"
                              : "AI Prediction"}
                      </span>
                    </div>
                    {saved ? (
                      <SavedSegmentationMeasurements annotation={saved} />
                    ) : (
                      <div className="font-mono text-[11px] text-fg-muted">
                        Mask extent {formatMm(longAxisMm)} × {formatMm(shortAxisMm)} mm
                      </div>
                    )}
                    {!saved && (
                      <SegmentationVolume
                        volumeMm3={preview.measurements.volumeMm3}
                        voxelCount={preview.voxelCount}
                      />
                    )}
                    <div className="font-mono text-[11px] text-fg-muted">
                      Ser{" "}
                      {resampleSavedVolume && saved
                        ? (studyMetadata?.series_metadata[saved.series_instance_uid]
                            ?.series_number ?? "—")
                        : (cardPreview?.seriesNumber ?? "—")}{" "}
                      · Slice {cardPreview?.sliceNumber ?? "—"}
                    </div>
                    <div className="font-mono text-[10px] capitalize text-fg-muted">
                      {saved
                        ? `Saved segment ${preview.segmentIndex}`
                        : `Segment ${preview.segmentIndex}`}
                    </div>
                    {askConfidence && !editingThis && (
                      <div data-testid="card-confidence" className={CONFIDENCE_SEPARATOR_CLASS}>
                        <ReaderConfidenceInput
                          compact
                          value={confidenceDrafts[preview.id] ?? ""}
                          disabled={
                            cardLocked ||
                            acceptBlocked ||
                            verdictPending ||
                            editingOther ||
                            !reportable
                          }
                          onChange={(value) =>
                            setConfidenceDrafts((current) => ({ ...current, [preview.id]: value }))
                          }
                          onSubmit={() => {
                            if (!cardLocked && !acceptBlocked && reportable && !editingOther)
                              void applyVerdict("approved");
                          }}
                        />
                      </div>
                    )}
                    {showSavedConfidence && saved && (
                      <CardConfidenceRow
                        value={parseReaderConfidence(saved.attributes[READER_CONFIDENCE_KEY])}
                        disabled={acceptDisabled || editButtonDisabled || !onEditPreview}
                        onSave={(value) => onSaveConfidence!(saved, value)}
                      />
                    )}
                    <div className="mt-auto flex flex-wrap gap-1.5 pt-1">
                      {!persistable && (
                        <span className="h-6 px-2 inline-flex items-center rounded-[2px] border border-dashed border-border-strong text-fg-muted font-mono text-[10px] uppercase tracking-wide">
                          View only
                        </span>
                      )}
                      {persistable && (
                        <button
                          type="button"
                          aria-label={
                            approved ? "Undo accepted segmentation" : "Accept segmentation"
                          }
                          aria-pressed={approved}
                          aria-disabled={undoAcceptBlocked || undefined}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (undoAcceptBlocked) {
                              emit("toast", { message: EDITING_UNDO_ACCEPT_MESSAGE });
                              return;
                            }
                            if (!reportable || confidenceMissing) return;
                            void applyVerdict("approved");
                          }}
                          disabled={acceptDisabled}
                          title={confidenceMissing ? READER_CONFIDENCE_HINT : undefined}
                          className={`h-6 px-2 whitespace-nowrap rounded-[2px] border font-mono text-[10px] uppercase tracking-wide transition-colors ${DISABLED_ACTION_CLASS} ${
                            undoAcceptBlocked
                              ? "bg-signal text-white border-signal opacity-50 cursor-not-allowed"
                              : approved
                                ? "bg-signal text-white border-signal"
                                : "border-border-strong text-fg-muted enabled:hover:text-fg enabled:hover:bg-surface-2"
                          }`}
                        >
                          {approved ? "Undo accept" : reportable ? "Accept" : "No size data"}
                        </button>
                      )}
                      {persistable && onEditPreview && (
                        <button
                          type="button"
                          aria-label="Edit segmentation"
                          disabled={editButtonDisabled}
                          onClick={(event) => {
                            event.stopPropagation();
                            setSelectedId(preview.id);
                            onEditPreview(preview);
                          }}
                          className={`h-6 px-2 whitespace-nowrap rounded-[2px] border border-border-strong text-fg-muted enabled:hover:text-fg enabled:hover:bg-surface-2 ${DISABLED_ACTION_CLASS} font-mono text-[10px] uppercase tracking-wide`}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label={denied ? "Undo declined segmentation" : "Decline segmentation"}
                        aria-pressed={denied}
                        onClick={(event) => {
                          event.stopPropagation();
                          void applyVerdict("denied");
                        }}
                        // Undo decline would leave the edit open on a pending card.
                        disabled={
                          cardLocked || verdictPending || editingOther || (editingThis && denied)
                        }
                        className={`h-6 px-2 whitespace-nowrap rounded-[2px] border font-mono text-[10px] uppercase tracking-wide transition-colors ${DISABLED_ACTION_CLASS} ${
                          denied
                            ? "bg-warn-tint text-warn border-warn/50"
                            : "border-border-strong text-fg-muted enabled:hover:text-fg enabled:hover:bg-surface-2"
                        }`}
                      >
                        {denied ? "Undo decline" : "Decline"}
                      </button>
                    </div>
                    <div className="font-mono text-[9px] uppercase tracking-wide text-fg-muted">
                      {editingThis ? (
                        <span className="text-signal-text-tint">
                          Editing · Accept saves your changes
                        </span>
                      ) : maskUnavailable ? (
                        <span className="text-warn">Mask unavailable · contact the a2z team</span>
                      ) : maskFailed ? (
                        <span className="text-warn">Mask failed to load · reload the case</span>
                      ) : maskLoading ? (
                        <MaskLoadingHint deadline={maskLoadingDeadlines[preview.id]} />
                      ) : imagesLoading ? (
                        "Images loading…"
                      ) : editHidden ? (
                        editHidden === "declined" ? (
                          "Undo decline to edit"
                        ) : (
                          "Show masks to edit"
                        )
                      ) : cardPreview ? (
                        "Click to open key slice"
                      ) : (
                        "Saved geometry unavailable"
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          {orderedSaved.map((annotation, index) => (
            <SavedSegmentationCard
              key={annotation.annotation_id}
              annotation={annotation}
              studyMetadata={studyMetadata}
              conditionLabels={conditionLabels}
              sequence={previews.length + index + 1}
              preview={cardPreviews[`saved-${annotation.annotation_id}`]}
              thumbnail={thumbnails[`saved-${annotation.annotation_id}`]}
              selected={selectedId === `saved-${annotation.annotation_id}`}
              editState={
                !annotationActive
                  ? unconfirmedPreviewId != null
                    ? "dimmed"
                    : undefined
                  : !editingPreviewId &&
                      !newAnnotationActive &&
                      editingSavedId === annotation.annotation_id
                    ? "active"
                    : "dimmed"
              }
              actionsDisabled={annotationActive || verdictsDisabled}
              onSaveConfidence={
                requireReaderConfidence && onSaveConfidence
                  ? (value) => onSaveConfidence(annotation, value)
                  : undefined
              }
              onReview={
                onReviewSavedAnnotation && !annotation.attributes.ai_prediction_condition
                  ? (review) => onReviewSavedAnnotation(annotation, review)
                  : undefined
              }
              onEdit={
                onEditSavedAnnotation
                  ? () => {
                      setEditingSavedId(annotation.annotation_id);
                      onEditSavedAnnotation(
                        annotation,
                        cardPreviews[`saved-${annotation.annotation_id}`] ?? null,
                      );
                    }
                  : undefined
              }
              onSelect={
                !annotationActive && onSavedPreviewSelect
                  ? (preview) => {
                      setSelectedId(`saved-${annotation.annotation_id}`);
                      onSavedPreviewSelect(preview);
                    }
                  : undefined
              }
            />
          ))}
          {annotationPlaceholder?.active && (
            <div
              ref={draftRef}
              data-testid="segmentation-annotation-draft"
              className="flex gap-3 p-2.5 text-left bg-surface border border-signal rounded-[2px]"
            >
              <div className="shrink-0 w-[132px] h-[132px] rounded-[2px] bg-bg-2 border border-dashed border-border-strong flex items-center justify-center">
                <span className="font-mono text-[10px] text-fg-muted">Annotation preview</span>
              </div>
              <div className="flex-1 min-w-0 flex flex-col gap-1.5 pt-0.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-xs text-fg">
                    {Object.values(conditionLabels).length === 1
                      ? formatSegmentationCondition(Object.values(conditionLabels)[0])
                      : "Segmentation"}{" "}
                    {cardCount + 1}
                  </span>
                  <span className="rounded-full bg-surface-2 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-fg-muted">
                    Annotation
                  </span>
                </div>
                <div className="font-mono text-[11px] text-fg-muted">Mask extent — × — mm</div>
                <div className="font-mono text-[11px] text-fg-muted">Ser — · Slice —</div>
                <SegmentationVolume />
              </div>
            </div>
          )}
        </div>
        {lockText && (
          <div
            role="status"
            data-testid="case-lock-overlay"
            className="absolute inset-0 z-10 flex items-center justify-center bg-bg-2/70 px-4"
          >
            <div className="flex max-w-xs flex-col gap-1 rounded-[2px] border border-border-strong bg-surface px-3 py-2.5 text-center shadow-[0_4px_12px_rgba(0,0,0,0.35)]">
              <p className="text-xs font-semibold text-fg">{lockText.title}</p>
              <p className="text-[11px] text-fg-muted">{lockText.undoHint}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
